import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import casesData from "../data/casesData";

export default function CaseDetail() {
  const { slug } = useParams();
  const c = casesData.find((c) => c.slug === slug);

  if (!c) {
    return (
      <section className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Case study not found</h1>
          <Link
            to="/case-studies"
            className="mt-6 inline-block text-cyber-blue hover:underline"
          >
            ← Back to case studies
          </Link>
        </div>
      </section>
    );
  }

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6 },
  };

  return (
    <article className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src={c.image}
          alt={c.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-20">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass text-xs font-semibold px-3 py-1 rounded-full text-cyber-blue inline-block mb-4"
          >
            {c.category}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl lg:text-6xl font-bold max-w-4xl"
          >
            {c.title}
          </motion.h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-0 mt-12 space-y-16">
        {/* Overview */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl font-bold text-cyber-blue mb-4">Overview</h2>
          <p className="text-gray-300 text-lg leading-relaxed">{c.overview}</p>
        </motion.section>

        {/* Challenge */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl font-bold text-tech-green mb-4">Challenge</h2>
          <p className="text-gray-300 text-lg leading-relaxed">{c.challenge}</p>
        </motion.section>

        {/* Solution */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl font-bold text-cyber-blue mb-4">Solution</h2>
          <p className="text-gray-300 text-lg leading-relaxed">{c.solution}</p>
        </motion.section>

        {/* Technologies */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl font-bold text-tech-green mb-4">
            Technologies Used
          </h2>
          <div className="flex flex-wrap gap-3">
            {c.technologies.map((tech) => (
              <span
                key={tech}
                className="glass text-sm px-4 py-2 rounded-full text-gray-200"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.section>

        {/* Results */}
        <motion.section {...fadeUp}>
          <h2 className="text-2xl font-bold text-cyber-blue mb-4">Results</h2>
          <div className="glass rounded-2xl p-8 border-cyber-blue/20">
            <p className="text-gray-300 text-lg leading-relaxed">{c.results}</p>
          </div>
        </motion.section>

        {/* Back link */}
        <motion.div {...fadeUp} className="pb-8">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-tech-green hover:text-cyber-blue transition-colors font-semibold"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back to Case Studies
          </Link>
        </motion.div>
      </div>
    </article>
  );
}
